import Head from 'next/head';
import React from 'react';
// import { useRouter } from 'next/router';
import PropTypes from 'prop-types';

import { useMiscellaneousData } from '../contexts/ssr-miscellaneous-context';

export default function SEOHead({ data, canonicalUrl }) {
  // const router = useRouter();
  const { miscellaneousData } = useMiscellaneousData();

  // const baseUrl = typeof window !== 'undefined' ? window.location.origin : '';
  // const currentUrl = `${baseUrl}${router.asPath}`;
  // const currentUrl = `https://${miscellaneousData?.hostname}/prep${router.asPath}`;

  // Canonical URL logic:
  // 1. If data.canonicalUrl exists, use it (server-provided takes precedence)
  // 2. Otherwise, use the canonicalUrl prop passed from parent component
  // 3. For /prep prefixed pages, canonical should point to non-prefixed version
  // 4. For non-prefixed pages, canonical should point to itself
  const finalCanonicalUrl = data?.canonicalUrl || canonicalUrl;

  // Determine robots content based on prefix
  const { isPrepPrefixed } = miscellaneousData || {};
  const isProduction = process.env.NEXT_PUBLIC_HOME_REMOTE_CONFIG_ENV === 'PROD';

  const robotsContent = isProduction && !isPrepPrefixed
    ? `${data?.noIndex ? 'noindex' : 'index'}, ${data?.noFollow ? 'nofollow' : 'follow'}` // Allow page-level control only in PROD for non-prefixed
    : 'noindex, nofollow'; // Force noindex for all other cases (non-PROD or /prep instances)

  // Parse custom meta tags if provided
  const parseCustomMetaTags = () => {
    if (!data?.customMetaTags || typeof data.customMetaTags !== 'string') {
      return null;
    }

    // Use regex to extract meta and link tags from the HTML string
    const metaTagRegex = /<meta\s+([^>]+)>/gi;
    const linkTagRegex = /<link\s+([^>]+)>/gi;

    const elements = [];
    let match;
    let keyIndex = 0;

    // Extract meta tags
    // eslint-disable-next-line no-cond-assign
    while ((match = metaTagRegex.exec(data.customMetaTags)) !== null) {
      const attributes = match[1];
      const props = {};

      // Parse attributes
      const attrRegex = /(\w+)=["']([^"']+)["']/g;
      let attrMatch;
      // eslint-disable-next-line no-cond-assign
      while ((attrMatch = attrRegex.exec(attributes)) !== null) {
        const [, attrName, attrValue] = attrMatch;
        props[attrName] = attrValue;
      }

      keyIndex += 1;
      elements.push(
        React.createElement('meta', { key: `custom-meta-${keyIndex}`, ...props }),
      );
    }

    // Extract link tags
    // eslint-disable-next-line no-cond-assign
    while ((match = linkTagRegex.exec(data.customMetaTags)) !== null) {
      const attributes = match[1];
      const props = {};

      // Parse attributes
      const attrRegex = /(\w+)=["']([^"']+)["']/g;
      let attrMatch;
      // eslint-disable-next-line no-cond-assign
      while ((attrMatch = attrRegex.exec(attributes)) !== null) {
        const [, attrName, attrValue] = attrMatch;
        props[attrName] = attrValue;
      }

      keyIndex += 1;
      elements.push(
        React.createElement('link', { key: `custom-link-${keyIndex}`, ...props }),
      );
    }

    return elements;
  };

  return (
    <Head>
      {/* Custom Meta Tags - if provided in meta object */}

      {/* Basic SEO */}
      {data?.seoTitle && (
      <title>{data?.seoTitle}</title>
      )}
      {data?.seoDescription && (
      <meta name="description" content={data?.seoDescription} />
      )}
      {data?.keywords?.trim()?.length > 0 && (
      <meta name="keywords" content={data?.keywords} />
      )}

      {/* Robots - overrides the default noindex, nofollow from _app.js */}
      <meta
        key="robots"
        name="robots"
        content={robotsContent}
      />

      {/* Open Graph */}
      {(data?.ogTags?.ogTitle) && (
      <meta property="og:title" content={data?.ogTags?.ogTitle} />
      )}
      {(data?.ogTags?.ogDescription) && (
      <meta property="og:description" content={data?.ogTags?.ogDescription} />
      )}
      {data?.ogTags?.ogType && (
      <meta property="og:type" content={data?.ogTags?.ogType} />
      )}
      {data?.ogTags?.ogImage && (
      <meta property="og:image" content={data?.ogTags?.ogImage} />
      )}
      {data?.ogTags?.ogImageAlt && (
      <meta property="og:image:alt" content={data?.ogTags?.ogImageAlt} />
      )}
      {data?.ogTags?.ogImageSecureUrl && (
      <meta property="og:image:secure_url" content={data?.ogTags?.ogImageSecureUrl} />
      )}
      {data?.ogTags?.ogLocale && (
      <meta property="og:locale" content={data?.ogTags?.ogLocale} />
      )}

      {/* ✅ Operations team using custom meta tags for this now */}
      {/* <meta property="og:url" content={finalCanonicalUrl || currentUrl} /> */}

      {/* Canonical URL */}
      {finalCanonicalUrl && (
        <link rel="canonical" href={finalCanonicalUrl} />
      )}
      {parseCustomMetaTags()}
      {/* JSON-LD Schema - supports multiple script tags */}
      {typeof data?.schemaMarkup === 'string'
            && data?.schemaMarkup?.trim()?.length > 0
            && (() => {
              const trimmedMarkup = data.schemaMarkup.trim();
              // Extract all JSON-LD script tags
              const scriptTagRegex = /<script[^>]*type=["']application\/ld\+json["'][^>]*>([\s\S]*?)<\/script>/gi;
              const matches = [...trimmedMarkup.matchAll(scriptTagRegex)];

              // If script tags found, render each one
              if (matches.length > 0) {
                return matches.map((match) => (
                  <script
                    key={`schema-${match[1].length}-${match[1].charCodeAt(0)}`}
                    type="application/ld+json"
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{ __html: match[1] }}
                  />
                ));
              }

              // Fallback: if no script tags, assume raw JSON and wrap it
              return (
                <script
                  type="application/ld+json"
                  // eslint-disable-next-line react/no-danger
                  dangerouslySetInnerHTML={{ __html: trimmedMarkup }}
                />
              );
            })()}
    </Head>
  );
}

SEOHead.propTypes = {
  data: PropTypes.shape({
    ogTags: PropTypes.shape({
      ogType: PropTypes.string,
      ogImage: PropTypes.string,
      ogTitle: PropTypes.string,
      ogLocale: PropTypes.string,
      ogImageAlt: PropTypes.string,
      ogDescription: PropTypes.string,
      ogImageSecureUrl: PropTypes.string,
    }),
    canonicalUrl: PropTypes.string,
    customMetaTags: PropTypes.string,
    noIndex: PropTypes.bool,
    noFollow: PropTypes.bool,
    seoTitle: PropTypes.string,
    schemaMarkup: PropTypes.string,
    seoDescription: PropTypes.string,
    breadcrumbTitle: PropTypes.string,
    keywords: PropTypes.string,
  }),
  canonicalUrl: PropTypes.string,
};

SEOHead.defaultProps = {
  canonicalUrl: undefined,
};
